import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, RefreshCw, Star, CheckCircle, XCircle, Trash2, TrendingUp, Clock, ThumbsUp } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import Sidebar from './admin/Sidebar';
import BlogManagement from './admin/BlogManagement';
import PortfolioManagement from './admin/PortfolioManagement';
import ContactsManagement from './admin/ContactsManagement';
import LinkGenerator from './LinkGenerator';
import SiteSettings from './admin/SiteSettings';
import PricingManagement from './admin/PricingManagement';
import AdvancedAnalytics from './admin/AdvancedAnalytics';
import ClientsManagement from './admin/ClientsManagement';
import UsersManagement from './admin/UsersManagement';
import Administration from './admin/Administration';

const AdminDashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const handleLogout = () => {
    onLogout();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const statsRes = await fetch(`${apiUrl}/api/reviews/stats`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      const pendingRes = await fetch(`${apiUrl}/api/reviews/pending`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        setPendingReviews(pendingData);
      }

      const allRes = await fetch(`${apiUrl}/api/reviews/all`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (allRes.ok) {
        const allData = await allRes.json();
        setAllReviews(allData);
      }
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId) => {
    setActionLoading(reviewId);
    try {
      const response = await fetch(`${apiUrl}/api/reviews/${reviewId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (response.ok) {
        await fetchData();
      }
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (reviewId) => {
    setActionLoading(reviewId);
    try {
      const response = await fetch(`${apiUrl}/api/reviews/${reviewId}/reject`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (response.ok) {
        await fetchData();
      }
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet avis définitivement ?')) {
      return;
    }
    setActionLoading(reviewId);
    try {
      const response = await fetch(`${apiUrl}/api/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (response.ok) {
        await fetchData();
      }
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold">En attente</span>,
      approved: <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">Approuvé</span>,
      rejected: <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">Rejeté</span>
    };
    return badges[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeSection={activeSection} onSectionChange={setActiveSection} />

      <div className="flex-1 ml-64">
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
          <div className="px-8 py-4 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Bienvenue, {user?.name || 'Admin'} 
                <span className="ml-2 text-sm font-normal text-gray-600">({user?.role || 'admin'})</span>
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {activeSection === 'dashboard' && 'Vue d\'ensemble de votre activité'}
                {activeSection === 'blog' && 'Gestion des Articles'}
                {activeSection === 'reviews' && 'Modération des Avis'}
                {activeSection === 'links' && 'Générateur de Liens'}
                {activeSection === 'portfolio' && 'Gestion du Portfolio'}
                {activeSection === 'pricing' && 'Gestion des Tarifs'}
                {activeSection === 'contacts' && 'Gestion des Contacts'}
                {activeSection === 'clients' && 'Gestion des Clients'}
                {activeSection === 'analytics' && 'Statistiques Avancées'}
                {activeSection === 'settings' && 'Paramètres du Site'}
                {activeSection === 'users' && 'Gestion des Utilisateurs'}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Button onClick={fetchData} className="bg-gray-100 hover:bg-gray-200 text-gray-700">
                <RefreshCw className="h-4 w-4 mr-2" />
                Actualiser
              </Button>
              <Button onClick={handleLogout} className="bg-red-500 hover:bg-red-600 text-white">
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </header>

        <div className="p-8">
          {activeSection === 'dashboard' && <DashboardOverview stats={stats} pendingReviews={pendingReviews} />}
          {activeSection === 'blog' && <BlogManagement apiKey="jwt-session" />}
          {activeSection === 'reviews' && (
            <ReviewsManagement
              pendingReviews={pendingReviews}
              allReviews={allReviews}
              onApprove={handleApprove}
              onReject={handleReject}
              onDelete={handleDelete}
              actionLoading={actionLoading}
              formatDate={formatDate}
              getStatusBadge={getStatusBadge}
            />
          )}
          {activeSection === 'links' && <LinkGenerator />}
          {activeSection === 'portfolio' && <PortfolioManagement />}
          {activeSection === 'contacts' && <ContactsManagement apiKey="jwt-session" />}
          {activeSection === 'pricing' && <PricingManagement apiKey="jwt-session" />}
          {activeSection === 'administration' && <Administration apiKey="jwt-session" />}
          {activeSection === 'clients' && <ClientsManagement apiKey="jwt-session" />}
          {activeSection === 'analytics' && <AdvancedAnalytics />}
          {activeSection === 'settings' && <SiteSettings apiKey="jwt-session" />}
          {activeSection === 'users' && <UsersManagement apiKey="jwt-session" />}
        </div>
      </div>
    </div>
  );
};

const DashboardOverview = ({ stats, pendingReviews }) => {
  if (!stats) return null;

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total</p>
                <p className="text-3xl font-bold mt-2">{stats.total}</p>
              </div>
              <TrendingUp className="h-10 w-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm font-medium">En attente</p>
                <p className="text-3xl font-bold mt-2">{stats.pending}</p>
              </div>
              <Clock className="h-10 w-10 text-yellow-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Approuvés</p>
                <p className="text-3xl font-bold mt-2">{stats.approved}</p>
              </div>
              <ThumbsUp className="h-10 w-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium">Rejetés</p>
                <p className="text-3xl font-bold mt-2">{stats.rejected}</p>
              </div>
              <XCircle className="h-10 w-10 text-red-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm font-medium">Moyenne</p>
                <p className="text-3xl font-bold mt-2 flex items-center gap-1">
                  {stats.average_rating} <Star className="h-6 w-6 fill-white" />
                </p>
              </div>
              <Star className="h-10 w-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-lg transition-shadow">
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-bold text-blue-900 mb-2">Avis en attente</h3>
            <p className="text-3xl font-bold text-blue-600">{pendingReviews.length}</p>
            <p className="text-sm text-blue-700 mt-2">Nécessitent une modération</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-lg transition-shadow">
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-bold text-green-900 mb-2">Blog actif</h3>
            <p className="text-3xl font-bold text-green-600">✓</p>
            <p className="text-sm text-green-700 mt-2">Articles en ligne</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:shadow-lg transition-shadow">
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-bold text-purple-900 mb-2">Portfolio</h3>
            <p className="text-3xl font-bold text-purple-600">8</p>
            <p className="text-sm text-purple-700 mt-2">Photos en ligne</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const ReviewsManagement = ({ pendingReviews, allReviews, onApprove, onReject, onDelete, actionLoading, formatDate, getStatusBadge }) => {
  const [activeTab, setActiveTab] = useState('pending');

  return (
    <div>
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-6 py-4 font-semibold transition-colors ${
                activeTab === 'pending'
                  ? 'border-b-2 border-amber-600 text-amber-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              En attente ({pendingReviews.length})
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`px-6 py-4 font-semibold transition-colors ${
                activeTab === 'all'
                  ? 'border-b-2 border-amber-600 text-amber-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Tous les avis ({allReviews.length})
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {(activeTab === 'pending' ? pendingReviews : allReviews).length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-600">
                {activeTab === 'pending' ? 'Aucun avis en attente 🎉' : 'Aucun avis pour le moment'}
              </p>
            </CardContent>
          </Card>
        ) : (
          (activeTab === 'pending' ? pendingReviews : allReviews).map((review) => (
            <Card key={review.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="flex gap-1">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                      {getStatusBadge(review.status)}
                      {review.event_type && (
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
                          {review.event_type}
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{review.name}</h3>
                    <p className="text-gray-700 mb-3 leading-relaxed">{review.comment}</p>
                    <p className="text-sm text-gray-500">{formatDate(review.created_at)}</p>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    {review.status === 'pending' && (
                      <>
                        <Button
                          onClick={() => onApprove(review.id)}
                          disabled={actionLoading === review.id}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approuver
                        </Button>
                        <Button
                          onClick={() => onReject(review.id)}
                          disabled={actionLoading === review.id}
                          className="bg-red-500 hover:bg-red-600 text-white"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Rejeter
                        </Button>
                      </>
                    )}
                    <Button
                      onClick={() => onDelete(review.id)}
                      disabled={actionLoading === review.id}
                      className="bg-gray-500 hover:bg-gray-600 text-white"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Supprimer
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

const ComingSoon = ({ title }) => {
  return (
    <Card>
      <CardContent className="py-24 text-center">
        <div className="max-w-md mx-auto">
          <div className="text-6xl mb-6">🚧</div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">{title}</h3>
          <p className="text-gray-600 mb-6">
            Cette fonctionnalité est en cours de développement et sera bientôt disponible.
          </p>
          <div className="inline-block px-6 py-3 bg-amber-100 text-amber-800 rounded-lg font-semibold">
            Prochainement disponible
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminDashboard;